import type { EventSource } from "../api/schemas";
import { applyClockEvent } from "../domain/clock";
import type { BreakQualification, ClockEventKind, ClockState } from "../domain/clock";
import type { IdGenerator } from "../domain/ids";
import type { GeoPoint } from "../domain/location";
import { failure, success } from "../domain/result";
import type { Result } from "../domain/result";
import { toEpochSeconds } from "../domain/time";
import { locationWaitMs, settingKeys } from "./LocalStore";
import type { LocalStore } from "./LocalStore";
import type { LocalEvent } from "./model";

/** Dependencies of {@link recordClockEvent}. */
export interface RecordEventDependencies {
  /** The outbox; only the clock offset is read (also the outbox of a terminal, T-173). */
  readonly store: Pick<LocalStore, "getSetting" | "addEvent">;
  readonly ids: IdGenerator;
  /** Device clock in epoch milliseconds. */
  readonly nowMs: () => number;
}

/** Input of {@link recordClockEvent}. */
export interface RecordEventInput {
  readonly ownerSubject: string;
  readonly kind: ClockEventKind;
  /** Current local clock state; the transition is checked before storing (INV-01). */
  readonly currentState: ClockState;
  readonly timeZoneId: string;
  readonly source: EventSource;
  readonly projectId?: string | null;
  /** Declared kind of a break start (ADR-0092); ignored for other events. */
  readonly breakQualification?: BreakQualification | null;
  /** The person recorded for on behalf (T-170); omitted for an own event. */
  readonly employeeId?: string | null;
  /**
   * Coarse location captured at the moment of an own event (T-175); ignored for an event on behalf,
   * whose location would be the one of the recording person.
   */
  readonly location?: GeoPoint | null;
  /**
   * Whether the position of this own step is asked for next (T-175): the entry then waits up to
   * {@link locationWaitMs} for it before it is sent, so a synchronization during the request does
   * not send it without (F-102). Ignored for an event on behalf.
   */
  readonly awaitLocation?: boolean;
}

/**
 * Records a clock event locally first (spec 8.1.1): validated against the local state, with a
 * client-side UUIDv7, the device time and the last measured clock offset. Synchronization happens
 * separately; the event is never corrected, only annotated (spec 8.1.5).
 *
 * @param dependencies - Store, identifier generator and clock.
 * @param input - What to record.
 * @returns The stored event or the transition error.
 */
export async function recordClockEvent(
  dependencies: RecordEventDependencies,
  input: RecordEventInput,
): Promise<Result<LocalEvent>> {
  const transition = applyClockEvent(input.currentState, input.kind);

  if (!transition.ok) {
    const rejected = failure<LocalEvent>(transition.error.code, transition.error.message);
    return rejected;
  }

  const storedOffset = await dependencies.store.getSetting(settingKeys.clockOffsetMs);
  const offset = storedOffset === null ? null : Number.parseInt(storedOffset, 10);
  const nowMs = dependencies.nowMs();
  const onBehalf = input.employeeId !== undefined && input.employeeId !== null;
  const event: LocalEvent = {
    id: dependencies.ids.next(),
    ownerSubject: input.ownerSubject,
    ...(input.employeeId === undefined || input.employeeId === null
      ? {}
      : { employeeId: input.employeeId }),
    kind: input.kind,
    occurredAt: toEpochSeconds(nowMs),
    timeZoneId: input.timeZoneId,
    source: input.source,
    projectId: input.projectId ?? null,
    clientClockOffsetMs: offset === null || Number.isNaN(offset) ? null : offset,
    breakQualification: input.kind === "BreakStart" ? (input.breakQualification ?? null) : null,
    ...(onBehalf || input.location === undefined || input.location === null
      ? {}
      : { location: input.location }),
    ...(onBehalf || input.awaitLocation !== true
      ? {}
      : { locationAwaitedUntilMs: nowMs + locationWaitMs }),
    createdAtMs: nowMs,
    syncStatus: "pending",
    syncedAtMs: null,
    attempts: 0,
    error: null,
    hints: [],
    suggestedAction: "None",
  };
  await dependencies.store.addEvent(event);
  const recorded = success(event);

  return recorded;
}
